import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { MarkdownField } from '../components/ui/MarkDown';
import { Pencil } from 'lucide-react';

const defaultPrivacyPolicy = `# Privacy Policy

Last Updated: January 25, 2025

## Introduction
Welcome to Fantasy Loser. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our website and services.

[Rest of the privacy policy content...]`;

const PrivacyPolicy: React.FC = () => {
  const { userRole } = useAuth();
  const [content, setContent] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'legal', 'privacy-policy');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setContent(data.content);
          setEditedContent(data.content);
        } else {
          // Initialize with default content
          await setDoc(docRef, { 
            content: defaultPrivacyPolicy,
            lastUpdated: new Date()
          });
          setContent(defaultPrivacyPolicy);
          setEditedContent(defaultPrivacyPolicy);
        }
      } catch (error) {
        console.error('Error fetching privacy policy:', error);
        setError('Failed to load privacy policy content. Please try again later.');
      }
    };

    fetchContent();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      const docRef = doc(db, 'legal', 'privacy-policy');
      await setDoc(docRef, {
        content: editedContent,
        lastUpdated: new Date()
      });
      
      setContent(editedContent);
      setIsEditing(false);
      alert('Privacy Policy updated successfully');
    } catch (error) {
      console.error('Error saving privacy policy:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (editedContent !== content) {
      setShowConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  if (content === null) {
    return (
      <main className="pageOuterWrapper">
        <Navbar />
        <div className="mainContentWrap">
          <div className="flex justify-center items-center h-full">
            {error || ''}
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="pageOuterWrapper">
      <Navbar />
      <div className="mainContentWrap">
        {error && (
          <div className="mb-4 text-red-500">{error}</div>
        )}
        
        {isEditing ? (
          <>
            <MarkdownField
              value={editedContent}
              onChange={(value) => setEditedContent(value || '')}
              label="Edit Privacy Policy"
              minHeight={500}
            />
            <div className="adminActionWrap">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="btnStyle"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="btnStyle btnFilled"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : (
          <div className="wrapper">
            <div className="privacyBlk">
              <div className="actionBtnWrap">
                {userRole === 'admin' && !isEditing && (
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="btnPrimary iconBtn"
                  >
                    <Pencil size={20} />
                  </button>
                )}          
              </div>
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          </div>
        )}

        {showConfirmDialog && (
          <div className="popupWrap">
            <div className="popupContBlk">
              <div className="headingTxt">Discard Changes?</div>
              <p>You have unsaved changes. Are you sure you want to discard them?</p>
              <div className="adminActionWrap">
                <button
                  onClick={() => {
                    setEditedContent(content);
                    setIsEditing(false);
                    setShowConfirmDialog(false);
                  }}
                  className="btnStyle"
                >
                  Discard Changes
                </button>
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="btnStyle btnFilled"
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

export default PrivacyPolicy;